import React from 'react';
import { TableSortState } from '../../../models/tableSortState';
import { useGetSegmentPushHistory } from '../../../hooks/useSegmentPush';
import { CircularProgress, Paper, Table, TableCell, TableRow, makeStyles } from '@material-ui/core';
import { HeaderCell, SearchBar, SecondaryAlert, TableBody, TableHead, SecondaryButton } from 'kagr-ui';
import * as styles from '../segments.module.scss';
import Pagination from '../../pagination/pagination';
import moment from 'moment';
import clsx from 'clsx';
import Paths from '../../../models/enums/paths';
import { Link, useHistory } from 'react-router-dom';
import PushStatus from '../../../models/enums/pushStatus';
import RunStatus from '../../../models/enums/runStatus';

type PushHistoryDetailsProps = {
  segmentId: string;
  canPush: boolean;
  clientIdForSegment: number;
};

enum PushHistorySortFields {
  TargetSystemName = 'targetSystemName',
  CampaignName = 'campaignName',
  DateLastRun = 'dateLastRun',
  Status = 'status',
  PushId = 'pushId',
  Type = 'isOneTime'
}

const pushHistoryHeaders = [
  {
    title: 'Push Id',
    orderField: PushHistorySortFields.PushId
  },
  {
    title: 'Destination System',
    orderField: PushHistorySortFields.TargetSystemName
  },
  {
    title: 'Campaign',
    orderField: PushHistorySortFields.CampaignName
  },
  {
    title: 'Type',
    orderField: PushHistorySortFields.Type
  },
  {
    title: 'Last Run',
    orderField: PushHistorySortFields.DateLastRun
  },
  {
    title: 'Status',
    orderField: PushHistorySortFields.Status
  }
];

const useStyles = makeStyles(() => ({
  tablePadding: {
    padding: '25px 0'
  },
  searchBar: {
    width: 180
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    padding: '10px 30px 0'
  },
  pushHistoryDetailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    fontWeight: 600,
    '& div': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      '& div': {
        padding: '0 15px'
      }
    }
  },
  pushPagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: '1px solid #DAD5D5'
  },
  pushDetailsCellCollapsed: {
    borderBottom: 'none'
  },
  pushDetailsCellExpand: {
    borderBottom: '1px solid rgba(224, 224, 224, 1);'
  },
  noHistoryContainer: {
    margin: '10rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }
  },
  paperColumn: {
    flexDirection: 'column'
  },
  pushHeader: {
    justifyContent: 'flex-end',
    marginTop: '8px'
  },
  linkText: {
    textDecoration: 'none',
    fontWeight: 500,
    color: '#006DA8'
  }
}));

const PushHistoryDetails = ({ segmentId, canPush, clientIdForSegment }: PushHistoryDetailsProps) => {
  const history = useHistory();
  const classes = useStyles();
  const [tableSort, setTableSort] = React.useState<TableSortState>({
    page: 1,
    pageSize: 15,
    orderColumn: PushHistorySortFields.DateLastRun,
    orderDirection: 'desc'
  });
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>('');

  const pushHistoryList = useGetSegmentPushHistory(segmentId, tableSort, activeSearchTerm);

  function reorderHandler(column: string, direction: 'asc' | 'desc') {
    setTableSort((state) => ({ ...state, orderColumn: column, orderDirection: direction }));
  }

  if (!!pushHistoryList.data && pushHistoryList.data.totalItems === 0) {
    return (
      <div className={classes.noHistoryContainer}>
        <div>
          <div className={styles.tableTitle}>The segment has not been pushed to a destination yet</div>
          {canPush && (
            <div className={clsx(styles.header, classes.pushHeader)}>
              <SecondaryButton
                text="Create New Push"
                handleClick={() => {
                  history.push(Paths.PushSegment.replace(':segmentId', segmentId));
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {canPush && (
        <div className={clsx(styles.header, classes.pushHeader)}>
          <SecondaryButton
            text="Create New Push"
            handleClick={() => {
              history.push(Paths.PushSegment.replace(':segmentId', segmentId));
            }}
          />
        </div>
      )}
      <section className={classes.tablePadding}>
        <Paper elevation={1} className={clsx(styles.paper, classes.paperColumn)}>
          <div className={styles.content}>
            <div className={classes.tableHeader}>
              <SearchBar
                value={searchValue}
                updateValue={setSearchValue}
                submitHandler={() => setActiveSearchTerm(searchValue)}
                placeholder="Search by Keyword"
                allowSubmitButton={false}
                className={classes.searchBar}
              />
            </div>
            {pushHistoryList.isLoading && !pushHistoryList.data && (
              <div className={styles.loading}>
                <CircularProgress />
              </div>
            )}
            {pushHistoryList.isError && (
              <div className={styles.error}>
                <SecondaryAlert type="error" message={`Failed to fetch results: ${pushHistoryList.error.message}`} />
              </div>
            )}
            {!!pushHistoryList.data && pushHistoryList.data.totalItems > 0 && (
              <div className={styles.tableWrapper}>
                <Table stickyHeader className={styles.table}>
                  <TableHead>
                    {pushHistoryHeaders.map((header) => (
                      <HeaderCell
                        key={header.title}
                        title={header.title}
                        orderField={header.orderField ? header.orderField : null}
                        orderColumn={header.orderField ? tableSort.orderColumn : null}
                        orderDirection={header.orderField ? tableSort.orderDirection : null}
                        reorderHandler={header.orderField ? reorderHandler : null}
                      />
                    ))}
                    <HeaderCell title=""></HeaderCell>
                  </TableHead>
                  <TableBody>
                    {pushHistoryList.data.totalItems === 0 && (
                      <tr>
                        <td colSpan={pushHistoryHeaders.length}>No results found</td>
                      </tr>
                    )}
                    {pushHistoryList.data.results.map((pushHistory) => (
                      <TableRow key={pushHistory.pushId}>
                        <TableCell padding="none">{pushHistory.pushId}</TableCell>
                        <TableCell padding="none">{pushHistory.targetSystemName}</TableCell>
                        <TableCell padding="none">{pushHistory.campaignName}</TableCell>
                        <TableCell padding="none">{pushHistory.isOneTime ? 'One time' : 'Automatic'}</TableCell>
                        <TableCell padding="none">
                          {pushHistory.dateLastRun ? moment(pushHistory.dateLastRun).format('M/D/YYYY h:mm:ss a') : ''}
                        </TableCell>
                        <TableCell padding="none">
                          {PushStatus[pushHistory.automaticPushStatus] ?? RunStatus[pushHistory.oneTimePushStatus]}
                        </TableCell>
                        <TableCell>
                          <Link
                            to={{
                              pathname: `/segments/${segmentId}/pushes/${pushHistory.pushId}`,
                              state: {
                                clientIdForSegment: clientIdForSegment
                              }
                            }}
                            className={classes.linkText}
                          >
                            View Push
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {pushHistoryList.data && (
              <div className={`${styles.paginationFooter} ${classes.pushPagination}`}>
                <Pagination
                  totalPages={Math.ceil(pushHistoryList.data.totalItems / tableSort.pageSize)}
                  pageSize={tableSort.pageSize}
                  currentPage={tableSort.page}
                  disabled={pushHistoryList.isPreviousData && pushHistoryList.isFetching}
                  handlePageChange={(page) => setTableSort((state) => ({ ...state, page }))}
                  handlePageSizeChange={(pageSize) =>
                    setTableSort((state) => ({
                      ...state,
                      page: 1,
                      pageSize
                    }))
                  }
                />
              </div>
            )}
          </div>
        </Paper>
      </section>
    </div>
  );
};

export default PushHistoryDetails;
